'use client'

import { useState, useEffect } from 'react'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FolderTree, Folder, ChevronRight, FilePlus } from 'lucide-react'
import { getAuthHeaders } from '@/lib/api-utils'
import { EditIssueModal } from '@/components/issues/edit-issue-modal'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

interface SubProject {
  id: number
  name: string
  identifier: string
}

interface Project {
  id: number
  name: string
  identifier: string
  parent?: { id: number; name: string }
  children?: SubProject[]
}

interface CreateIssueSidebarProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export function CreateIssueSidebar({ isOpen, onClose, onSuccess }: CreateIssueSidebarProps) {
  const t = useTranslations('issuesPage')
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null)
  const [showIssueModal, setShowIssueModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (isOpen) {
      fetchProjects()
      setSelectedProjectId(null)
      setShowIssueModal(false)
      setSearchTerm('')
    }
  }, [isOpen])

  const fetchProjects = async () => {
    setLoading(true)
    try {
      const authHeaders = getAuthHeaders()

      let allProjects: any[] = []
      let currentOffset = 0
      const PROJECTS_PER_PAGE = 100
      const maxIterations = 50
      let iterations = 0

      // Fetch ALL projects to build complete hierarchy (same as projects page)
      while (iterations < maxIterations) {
        iterations++

        const response = await fetch(`/api/projects?limit=${PROJECTS_PER_PAGE}&offset=${currentOffset}`, {
          headers: authHeaders
        })

        if (!response.ok) {
          throw new Error('Failed to fetch projects')
        }

        const batchData = await response.json()

        if (!batchData.projects || batchData.projects.length === 0) {
          break
        }

        allProjects = [...allProjects, ...batchData.projects]

        if (batchData.projects.length < PROJECTS_PER_PAGE) {
          break
        }

        currentOffset += PROJECTS_PER_PAGE
      }

      // Build hierarchical tree structure (same as projects page)
      const projectTree = buildProjectTree(allProjects)
      setProjects(projectTree)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  // Build hierarchical project tree structure exactly like projects page
  const buildProjectTree = (projects: any[]): Project[] => {
    const projectMap = new Map<number, Project>()
    const rootProjects: Project[] = []

    // First pass: create map with all projects and initialize children arrays
    projects.forEach(project => {
      projectMap.set(project.id, {
        id: project.id,
        name: project.name,
        identifier: project.identifier,
        parent: project.parent,
        children: []
      })
    })

    // Second pass: build parent-child relationships from API data
    projects.forEach(project => {
      const projectNode = projectMap.get(project.id)!

      if (project.parent?.id) {
        // This is a child project - add to parent's children
        const parent = projectMap.get(project.parent.id)
        if (parent) {
          parent.children = parent.children || []
          // Prevent duplicates
          if (!parent.children.find(child => child.id === projectNode.id)) {
            parent.children.push(projectNode)
          }
        } else {
          // Parent not in current dataset, treat as root
          rootProjects.push(projectNode)
        }
      } else {
        // This is a root project
        rootProjects.push(projectNode)
      }
    })

    // Sort projects alphabetically at each level
    const sortProjects = (projects: Project[]) => {
      projects.sort((a, b) => a.name.localeCompare(b.name))
      projects.forEach(project => {
        if (project.children && project.children.length > 0) {
          sortProjects(project.children)
        }
      })
    }

    sortProjects(rootProjects)
    return rootProjects
  }

  const handleProjectSelect = (projectId: number) => {
    setSelectedProjectId(projectId)
  }

  const handleContinue = () => {
    if (selectedProjectId) {
      setShowIssueModal(true)
    }
  }

  const handleIssueModalClose = () => {
    setShowIssueModal(false)
    setSelectedProjectId(null)
    onClose()
  }

  const handleIssueSuccess = () => {
    setShowIssueModal(false)
    setSelectedProjectId(null)
    onSuccess?.()
    onClose()
  }

  // Recursive filter function (same as projects page)
  const filterProjectTree = (projects: Project[], searchTerm: string): Project[] => {
    return projects.reduce<Project[]>((filtered, project) => {
      const matchesSearch = !searchTerm ||
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.identifier.toLowerCase().includes(searchTerm.toLowerCase())

      const filteredChildren = project.children ?
        filterProjectTree(project.children, searchTerm) : []

      // Include project if it matches OR has matching children
      if (matchesSearch || filteredChildren.length > 0) {
        filtered.push({
          ...project,
          children: filteredChildren
        })
      }

      return filtered
    }, [])
  }

  // Helper to flatten tree for finding selected project
  const flattenProjectTree = (project: Project): Project[] => {
    const flattened = [project]
    if (project.children) {
      project.children.forEach(child => {
        flattened.push(...flattenProjectTree(child))
      })
    }
    return flattened
  }

  // Filter projects based on search (recursive through tree)
  const filteredProjects = searchTerm
    ? filterProjectTree(projects, searchTerm)
    : projects

  const selectedProject = projects
    .flatMap(p => flattenProjectTree(p))
    .find(p => p.id === selectedProjectId)

  // Recursive project item renderer
  const renderProjectItem = (project: Project, level: number = 0): JSX.Element => {
    const hasChildren = project.children && project.children.length > 0
    const paddingLeft = `${level * 1}rem`

    if (hasChildren) {
      return (
        <AccordionItem key={project.id} value={`project-${project.id}`} className="border-b">
          <AccordionTrigger className="hover:no-underline py-3" style={{ paddingLeft }}>
            <div className="flex items-center gap-2 flex-1">
              <FolderTree className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-foreground">{project.name}</span>
              <span className="text-xs text-muted-foreground">({project.children!.length})</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-1">
              {/* Parent Project Selectable */}
              <button
                onClick={() => handleProjectSelect(project.id)}
                className={`w-full flex items-center gap-2 p-2 rounded-md text-sm transition-colors text-left ${
                  selectedProjectId === project.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-foreground'
                }`}
                style={{ paddingLeft: `${(level + 1) * 1}rem` }}
              >
                <Folder className="h-4 w-4" />
                <span>{project.name}</span>
                {selectedProjectId === project.id && <ChevronRight className="h-4 w-4 ml-auto" />}
              </button>

              {/* Recursively render children */}
              {project.children!.map((child) => renderProjectItem(child, level + 1))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )
    } else {
      return (
        <button
          key={project.id}
          onClick={() => handleProjectSelect(project.id)}
          className={`w-full flex items-center gap-2 p-3 rounded-md text-sm transition-colors text-left ${
            selectedProjectId === project.id
              ? 'bg-primary text-primary-foreground'
              : 'hover:bg-muted text-foreground'
          }`}
          style={{ paddingLeft }}
        >
          <Folder className="h-4 w-4" />
          <span className="font-medium">{project.name}</span>
          {selectedProjectId === project.id && <ChevronRight className="h-4 w-4 ml-auto" />}
        </button>
      )
    }
  }

  return (
    <>
      <GlobalSidebar
        isOpen={isOpen}
        onClose={onClose}
        title={t('createIssueTitle')}
        subtitle={t('createIssueSubtitle')}
        icon={<FilePlus className="h-5 w-5" />}
        width="md"
        footer={
          <div className="flex justify-end">
            <Button
              onClick={handleContinue}
              disabled={!selectedProjectId}
              className="w-full sm:w-auto"
            >
              {t('continue')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
            {/* Search */}
            <div>
              <Input
                type="text"
                placeholder={t('searchProjectsPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Selected Project Display */}
            {selectedProject && (
              <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">{t('selectedProjectLabel')}</div>
                <div className="font-medium text-foreground flex items-center gap-2">
                  <Folder className="h-4 w-4" />
                  {selectedProject.name}
                </div>
              </div>
            )}

            {/* Projects Accordion */}
            <div className="space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-sm text-muted-foreground">{t('loadingProjects')}</div>
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-sm text-muted-foreground">
                    {searchTerm ? t('noProjectsFound') : t('noProjectsAvailable')}
                  </div>
                </div>
              ) : (
                <Accordion type="multiple" className="w-full">
                  {filteredProjects.map((project) => renderProjectItem(project, 0))}
                </Accordion>
              )}
            </div>
          </div>
        </GlobalSidebar>

        {/* Issue Creation Modal */}
        {showIssueModal && selectedProjectId && (
          <EditIssueModal
            isOpen={showIssueModal}
            onClose={handleIssueModalClose}
            projectId={selectedProjectId}
            onSuccess={handleIssueSuccess}
          />
        )}
      </>
    )
}
